import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, Save } from "lucide-react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { FormData } from "../types";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface Template {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  currency: "EUR" | "GBP" | "USD";
  created_at?: string;
  updated_at?: string;
  user_id?: string;
}

interface InvoiceLineItemsProps {
  form: UseFormReturn<FormData>;
}

export const InvoiceLineItems = ({ form }: InvoiceLineItemsProps) => {
  const queryClient = useQueryClient();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "lineItems",
  });
  const { t } = useTranslation();

  const { fields: taxFields, append: appendTax, remove: removeTax } = useFieldArray({
    control: form.control,
    name: "taxes",
  });

  const getCurrencySymbol = (currency: string): string => {
    switch (currency) {
      case "EUR":
        return "€";
      case "GBP":
        return "£";
      case "USD":
        return "$";
      default:
        return "€";
    }
  };

  const saveTemplateMutation = useMutation({
    mutationFn: async (index: number) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const lineItem = form.getValues(`lineItems.${index}`);
      const currency = form.getValues("currency");
      const templateData = {
        user_id: user.id,
        description: lineItem.description,
        quantity: lineItem.quantity,
        unit_price: lineItem.unitPrice,
        currency: currency as "EUR" | "GBP" | "USD",
      };

      const { error } = await supabase
        .from("line_item_templates")
        .insert(templateData);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["line-item-templates"] });
      toast.success("Line item template saved successfully");
    },
    onError: (error) => {
      toast.error("Failed to save template", {
        description: error.message,
      });
    },
  });

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from("line_item_templates")
        .select("*");

      if (error) throw error;
      setTemplates(data || []);
    } catch (error: any) {
      toast.error("Failed to load templates", {
        description: error.message,
      });
    }
  };

  const [templates, setTemplates] = useState<Template[]>([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleTemplateSelect = (templateId: string, index: number) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;

    form.setValue(`lineItems.${index}.description`, template.description);
    form.setValue(`lineItems.${index}.quantity`, template.quantity);
    form.setValue(`lineItems.${index}.unitPrice`, template.unit_price);
  };

  const calculateSubtotal = () => {
    return fields.reduce((total, _, index) => {
      const quantity = form.watch(`lineItems.${index}.quantity`) || 0;
      const unitPrice = form.watch(`lineItems.${index}.unitPrice`) || 0;
      return total + (quantity * unitPrice);
    }, 0);
  };

  const calculateTaxAmount = (subtotal: number) => {
    return taxFields.reduce((total, _, index) => {
      const percentage = form.watch(`taxes.${index}.percentage`) || 0;
      return total + (subtotal * percentage / 100);
    }, 0);
  };

  const subtotal = calculateSubtotal();
  const taxAmount = calculateTaxAmount(subtotal);
  const total = subtotal + taxAmount;

  return (
    <Card className="border-2 border-[#34375B]">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-[#34375B]">{t("invoice_line_items.title", "Invoice Line Items")}</CardTitle>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="hover:bg-[#5CC2BB] hover:text-[#34375B] hover:border-transparent hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all duration-200"
          onClick={() => append({ description: "", quantity: 1, unitPrice: 0 })}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("invoice_line_items.add_item", "Add Item")}
        </Button>

        {/* className="hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all duration-200" */}

      </CardHeader>
      <CardContent className="space-y-6">
        {fields.map((field, index) => (
          <div key={field.id} className="space-y-4 p-4 border rounded-lg relative">
            <div className="absolute top-2 right-2 flex gap-2">
              {fields.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:bg-transparent text-[#F39D96] hover:text-[#F44336]"
                  onClick={() => remove(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="hover:bg-transparent p-0 text-[#9397A8] hover:text-[#34375B]"
                onClick={() => saveTemplateMutation.mutate(index)}
              >
                <Save className="h-4 w-4 transition-colors" />
              </Button>
            </div>

            <div className="max-w-xs">
              <FormItem>
                <FormLabel>{t("common.load_from_template", "Load from Template")}</FormLabel>
                <Select onValueChange={(value) => handleTemplateSelect(value, index)}>
                  <FormControl>
                    <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                      <SelectValue placeholder={t("invoice_line_items.select_a_template", "Select a template")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-white border border-gray-300 p-2 rounded-md">
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.description} ({template.quantity} × {template.unit_price} {getCurrencySymbol(template.currency)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            </div>

            <FormField
              control={form.control}
              name={`lineItems.${index}.description`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("invoice_line_items.description", "Description")}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t("invoice_line_items.description_placeholder", "Service or product description")}
                      className="placeholder:text-[#9397A8] text-[#34375B] bg-white border border-gray-300 p-2 rounded-md"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name={`lineItems.${index}.quantity`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("invoice_line_items.quantity", "Quantity")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        className="placeholder:text-[#9397A8] text-[#34375B] bg-white border border-gray-300 p-2 rounded-md"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={`lineItems.${index}.unitPrice`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("invoice_line_items.unit_price", "Unit Price")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        className="placeholder:text-[#9397A8] text-[#34375B] bg-white border border-gray-300 p-2 rounded-md"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("invoice_line_items.currency", "Currency")}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                          <SelectValue placeholder={t("templates.select_currency", "Select currency")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white border border-gray-300 p-2 rounded-md">
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="text-right">
              <p className="text-sm text-muted-foreground">
                {t("invoice_line_items.item_total", "Item Total: ")}{getCurrencySymbol(form.watch("currency"))}{((form.watch(`lineItems.${index}.quantity`) || 0) * (form.watch(`lineItems.${index}.unitPrice`) || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        ))}

        <div className="space-y-4 p-4 border rounded-lg">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-[#34375B]">{t("invoice_line_items.tax_information", "Tax Information")}</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="hover:bg-[#5CC2BB] hover:text-[#34375B] hover:border-transparent hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all duration-200"
              onClick={() => appendTax({ name: "", percentage: 0 })}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("invoice_line_items.add_tax", "Add Tax")}
            </Button>
          </div>

          {taxFields.map((field, index) => (
            <div key={field.id} className="grid grid-cols-1 md:grid-cols-2 gap-4 relative">
              <FormField
                control={form.control}
                name={`taxes.${index}.name`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("invoice_line_items.tax_name", "Tax Name")}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., IVA" className="placeholder:text-[#9397A8] text-[#34375B] bg-white border border-gray-300 p-2 rounded-md" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex gap-2 items-start">
                <FormField
                  control={form.control}
                  name={`taxes.${index}.percentage`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{t("invoice_line_items.tax_percentage", "Tax Percentage")}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          max="100"
                          className="placeholder:text-[#9397A8] text-[#34375B] bg-white border border-gray-300 p-2 rounded-md"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-destructive mt-8 hover:bg-transparent text-[#F39D96] hover:text-[#F44336]"
                  onClick={() => removeTax(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          {taxFields.length === 0 && (
            <p className="text-sm font-thin text-muted-foreground text-center py-2">
              {t("invoice_line_items.no_taxes", "No taxes added. Click 'Add Tax' to add a tax item.")}
            </p>
          )}
        </div>

        <div className="text-right border-t pt-4">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {t("invoice_line_items.subtotal", "Total: ")}{getCurrencySymbol(form.watch("currency"))}{subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            {taxFields.map((field, index) => {
              const percentage = form.watch(`taxes.${index}.percentage`) || 0;
              const taxName = form.watch(`taxes.${index}.name`) || 'Tax';
              const amount = subtotal * (percentage / 100);
              return (
                <p key={field.id} className="text-sm text-muted-foreground">
                  {taxName} ({percentage}%): {getCurrencySymbol(form.watch("currency"))}{amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              );
            })}
            <p className="text-lg font-semibold">
              {/* {t("invoice_line_items.total", "Total: €")}{total.toFixed(2)} */}
              {t("invoice_line_items.total", "Total: ")}{getCurrencySymbol(form.watch("currency"))}{total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
